/**
 * Modal panels: engine settings, raw DSP view, diagnostics, render/export,
 * measurement lab, test signals, debug console.
 */

import { useEffect, useRef, useState } from 'react';
import { engine } from '../../audio/engine';
import { registry, checkIntegrity } from '../../database';
import * as storage from '../../database/storage';
import type { AmpProfile, DacProfile, OutputProfile } from '../../types/hardware';
import { NL_NAMES } from '../../types/compiled';
import { useApp, resolveCable } from '../../stores/appStore';
import { renderOffline, measureChain, type SweepMeasurement } from '../../render/offline';
import { writeWav, downloadBytes, readWav, type WavFormat } from '../../render/wav';
import { encodeFlac, verifyFlacHeader } from '../../render/flac';
import { runDiagnostics, type DiagnosticResult } from '../../audio/diagnostics';
import { importProfileJson, exportProfileJson } from '../../measurement/importers';
import { Note, SectionTitle, Segmented, Slider, Spinner, Stat, Toggle, fmtBytes, fmtHz } from '../components/primitives';
import { FrequencyPlot, ImpulsePlot } from '../components/plots';

export function Panels() {
  const openPanel = useApp((s) => s.openPanel);
  const setPanel = useApp((s) => s.setPanel);
  if (!openPanel || openPanel === 'library') return null;

  const titles: Record<string, string> = {
    engine: 'Engine, quality and performance',
    rawdsp: 'Raw DSP state',
    diagnostics: 'Audio engine diagnostics',
    convert: 'Offline render and export',
    measure: 'Measurement lab',
    test: 'Test signal generator',
    console: 'Debug console',
    project: 'Project',
  };

  return (
    <div className="modal-overlay" onClick={() => setPanel(null)}>
      <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={titles[openPanel]}>
        <div className="modal-head">
          <h3>{titles[openPanel] ?? openPanel}</h3>
          <div className="spacer" />
          <button className="btn sm" onClick={() => setPanel(null)}>
            Close
          </button>
        </div>
        <div className="modal-body">
          {openPanel === 'engine' && <EnginePanel />}
          {openPanel === 'rawdsp' && <RawDspPanel />}
          {openPanel === 'diagnostics' && <DiagnosticsPanel />}
          {openPanel === 'convert' && <RenderPanel />}
          {openPanel === 'measure' && <MeasurementPanel />}
          {openPanel === 'test' && <TestSignalPanel />}
          {openPanel === 'console' && <ConsolePanel />}
          {openPanel === 'project' && <ProjectPanel />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function EnginePanel() {
  const e = useApp((s) => s.project.engine);
  const setEngine = useApp((s) => s.setEngine);
  const compiled = useApp((s) => s.compiled.A);
  const caps = engine.capabilities;

  return (
    <>
      <SectionTitle>Realism</SectionTitle>
      <Segmented
        value={e.realism}
        onChange={(v) => setEngine({ realism: v })}
        options={[
          { value: 'standard', label: 'Standard', title: 'Lightest DSP that still models the hardware' },
          { value: 'high', label: 'High', title: 'Higher oversampling and longer filters' },
          { value: 'maximum', label: 'Maximum', title: 'Fidelity over CPU' },
        ]}
        ariaLabel="Realism"
      />
      <p className="dim small">
        Realism controls how much computation is spent on each model. Maximum raises the oversampling factor of every
        nonlinear stage and lengthens the transducer FIRs, which reduces aliasing in the distortion products and
        improves low-frequency accuracy.
      </p>

      <SectionTitle>Performance</SectionTitle>
      <Segmented
        value={e.performance}
        onChange={(v) => setEngine({ performance: v })}
        options={[
          { value: 'low-latency', label: 'Low latency' },
          { value: 'balanced', label: 'Balanced' },
          { value: 'maximum-quality', label: 'Maximum quality' },
        ]}
        ariaLabel="Performance"
      />

      <div className="field" style={{ marginTop: 8 }}>
        <label>Oversampling</label>
        <Segmented
          small
          value={e.oversample}
          onChange={(v) => setEngine({ oversample: v })}
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 1, label: '1x' },
            { value: 2, label: '2x' },
            { value: 4, label: '4x' },
            { value: 8, label: '8x' },
          ]}
          ariaLabel="Oversampling"
        />
      </div>

      <Toggle label="Follow the source file's sample rate" checked={e.followSourceRate} onChange={(v) => setEngine({ followSourceRate: v })} />
      <Toggle label="Safety limiter" checked={e.safetyLimiter} onChange={(v) => setEngine({ safetyLimiter: v })} />
      <Toggle label="Experimental models" checked={e.experimental} onChange={(v) => setEngine({ experimental: v })} />

      <SectionTitle>What the browser is actually doing</SectionTitle>
      <div className="stat-grid">
        <Stat label="Context rate" value={`${caps.contextSampleRate} Hz`} />
        <Stat label="Requested" value={caps.requestedSampleRate ? `${caps.requestedSampleRate} Hz` : 'default'} />
        <Stat label="Base latency" value={caps.baseLatencySec !== null ? `${(caps.baseLatencySec * 1000).toFixed(2)} ms` : 'unknown'} />
        <Stat label="Output latency" value={caps.outputLatencySec !== null ? `${(caps.outputLatencySec * 1000).toFixed(1)} ms` : 'unknown'} />
        <Stat label="DSP latency" value={compiled ? `${((compiled.latencySamples / caps.contextSampleRate) * 1000).toFixed(2)} ms` : '--'} />
        <Stat label="AudioWorklet" value={caps.audioWorklet ? 'yes' : 'no'} tone={caps.audioWorklet ? 'ok' : 'danger'} />
        <Stat label="SharedArrayBuffer" value={caps.sharedArrayBuffer && caps.crossOriginIsolated ? 'yes' : 'no'} />
        <Stat label="WASM SIMD" value={caps.wasmSimd ? 'yes' : 'no'} />
        <Stat label="WebGPU" value={caps.webgpu ? 'available' : 'no'} />
        <Stat label="Offline render" value={caps.offlineAudioContext ? 'yes' : 'no'} />
      </div>
      <Note tone="warn">
        The browser and operating system may resample the output to whatever the audio device is running at, and this
        page has no way to observe or prevent that. Nothing here claims a bit-perfect path, because a web page cannot
        establish one.
      </Note>
    </>
  );
}

// ---------------------------------------------------------------------------

function RawDspPanel() {
  const activeSystem = useApp((s) => s.activeSystem);
  const compiled = useApp((s) => s.compiled[activeSystem]);
  const status = useApp((s) => s.status[activeSystem]);
  const caps = engine.capabilities;
  const [, force] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      engine.pollStatus();
      force((n) => n + 1);
    }, 500);
    return () => clearInterval(t);
  }, []);

  if (!compiled) return <p className="dim">No compiled chain.</p>;
  const fs = caps.contextSampleRate || 48000;
  const blockMs = (128 / fs) * 1000;

  return (
    <>
      <SectionTitle>Worklet health</SectionTitle>
      <table className="data">
        <thead>
          <tr>
            <th>Stage</th>
            <th>Profile</th>
            <th className="num">CPU / block</th>
            <th className="num">Budget</th>
            <th className="num">Peak</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {(['dac', 'amp', 'output', 'spatial'] as const).map((k) => {
            const st = status?.[k];
            const load = st?.cpuMs != null ? (st.cpuMs / blockMs) * 100 : null;
            return (
              <tr key={k}>
                <td>{k}</td>
                <td className="dim">{st?.profileId ?? '--'}</td>
                <td className="num">{st?.cpuMs != null ? `${st.cpuMs.toFixed(3)} ms` : 'n/a'}</td>
                <td className="num" style={{ color: load != null && load > 60 ? 'var(--warn)' : undefined }}>
                  {load != null ? `${load.toFixed(1)}%` : '--'}
                </td>
                <td className="num">{st ? (20 * Math.log10(Math.max(1e-6, st.peak))).toFixed(1) : '--'}</td>
                <td>
                  {st?.transitioning ? <span className="warnc">transitioning</span> : st?.bypassed ? 'bypassed' : 'active'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!engine.capabilities.audioWorklet && <Note tone="danger">AudioWorklet is unavailable.</Note>}
      <p className="faint tiny">
        Render quantum is 128 frames = {blockMs.toFixed(3)} ms at {fs} Hz. CPU per block is measured inside the worklet
        where the environment exposes a high-resolution clock; where it does not, this reads n/a rather than showing a
        number that would be made up.
      </p>

      <SectionTitle>Compiled stages</SectionTitle>
      <div className="stat-grid">
        <Stat label="DAC oversample" value={`${compiled.dac.oversample}x`} />
        <Stat label="DAC FIR" value={compiled.dac.firTaps?.length ?? (compiled.dac.nosHold ? 'hold' : 0)} sub="taps" />
        <Stat label="DAC decimator" value={compiled.dac.downTaps?.length ?? 0} sub="taps" />
        <Stat label="Analog EQ" value={compiled.dac.analogEq.length / 5} sub="biquads" />
        <Stat label="DAC model" value={NL_NAMES[compiled.dac.nonlinear.kind]} />
        <Stat label="Eff. bits" value={compiled.dac.effectiveBits || 'float'} />
        <Stat label="Amp oversample" value={`${compiled.amp.oversample}x`} />
        <Stat label="Amp EQ" value={compiled.amp.eq.length / 5} sub="biquads" />
        <Stat label="Load EQ" value={compiled.amp.loadEq.length / 5} sub="biquads" />
        <Stat label="Amp model" value={NL_NAMES[compiled.amp.nonlinear.kind]} />
        <Stat label="Output FIR" value={compiled.output.firTaps?.length ?? 0} sub="taps" />
        <Stat label="Output EQ" value={compiled.output.eq.length / 5} sub="biquads" />
        <Stat label="Output model" value={NL_NAMES[compiled.output.nonlinear.kind]} />
        <Stat label="Reflections" value={compiled.spatial.reflections.length / 4} />
        <Stat label="Total latency" value={compiled.latencySamples.toFixed(0)} sub={`${((compiled.latencySamples / fs) * 1000).toFixed(2)} ms`} />
        <Stat label="Chain gain" value={`${(20 * Math.log10(Math.max(1e-9, compiled.chainGain1k))).toFixed(2)} dB`} sub="@ 1 kHz" />
      </div>

      <SectionTitle>Gain at every stage</SectionTitle>
      <table className="data">
        <tbody>
          <tr><td>DAC output scale</td><td className="num">{(20 * Math.log10(Math.max(1e-9, compiled.dac.outputScale))).toFixed(2)} dB</td></tr>
          <tr><td>Amplifier voltage gain</td><td className="num">{(20 * Math.log10(Math.max(1e-9, compiled.amp.gainLinear))).toFixed(2)} dB</td></tr>
          <tr><td>Amplifier normalisation</td><td className="num">{(20 * Math.log10(Math.max(1e-9, compiled.amp.postScale))).toFixed(2)} dB</td></tr>
          <tr><td>Clip ceiling (internal)</td><td className="num">{(20 * Math.log10(Math.max(1e-9, compiled.amp.clipCeiling))).toFixed(2)} dB</td></tr>
          <tr><td>Transducer level scale</td><td className="num">{(20 * Math.log10(Math.max(1e-9, compiled.output.levelScale))).toFixed(2)} dB</td></tr>
        </tbody>
      </table>
    </>
  );
}

// ---------------------------------------------------------------------------

function DiagnosticsPanel() {
  const [results, setResults] = useState<DiagnosticResult[] | null>(null);
  const [running, setRunning] = useState(false);
  const state = useApp.getState();

  const run = async () => {
    setRunning(true);
    setResults(null);
    try {
      const r = await runDiagnostics({
        buffer: state.buffer,
        chain: state.project.systemA,
        engine: state.project.engine,
        dac: registry.get(state.project.systemA.dacId) as DacProfile | null,
        amp: registry.get(state.project.systemA.ampId) as AmpProfile | null,
        output: registry.get(state.project.systemA.outputId) as OutputProfile | null,
      });
      setResults(r);
    } finally {
      setRunning(false);
    }
  };

  const integrity = checkIntegrity();
  const pass = results?.filter((r) => r.status === 'PASS').length ?? 0;
  const warn = results?.filter((r) => r.status === 'WARNING').length ?? 0;
  const fail = results?.filter((r) => r.status === 'FAIL').length ?? 0;

  return (
    <>
      <div className="row" style={{ marginBottom: 10 }}>
        <button className="btn primary" onClick={() => void run()} disabled={running}>
          {running ? <Spinner /> : '▶'} Run diagnostics
        </button>
        {results && (
          <span className="mono small">
            <span className="okc">{pass} pass</span> · <span className="warnc">{warn} warning</span> ·{' '}
            <span className={fail ? 'dangerc' : 'dim'}>{fail} fail</span>
          </span>
        )}
      </div>

      {results && (
        <table className="data">
          <thead>
            <tr>
              <th>Test</th>
              <th>Status</th>
              <th>Detail</th>
              <th className="num">ms</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.name}>
                <td>{r.name}</td>
                <td className={r.status === 'PASS' ? 'okc' : r.status === 'WARNING' ? 'warnc' : 'dangerc'}>{r.status}</td>
                <td className="dim">{r.detail}</td>
                <td className="num dim">{r.ms?.toFixed(1) ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <SectionTitle>Database integrity</SectionTitle>
      <div className="stat-grid">
        <Stat label="DACs" value={integrity.counts.dac} />
        <Stat label="Amplifiers" value={integrity.counts.amp} />
        <Stat label="Speakers" value={integrity.counts.speaker} />
        <Stat label="Headphones" value={integrity.counts.headphone} />
        <Stat label="Cables" value={integrity.counts.cable} />
        <Stat label="Total" value={integrity.counts.total} />
        <Stat label="Duplicate IDs" value={integrity.duplicateIds.length} tone={integrity.duplicateIds.length ? 'danger' : 'ok'} />
        <Stat label="Duplicate brands" value={integrity.duplicateManufacturers.length} tone={integrity.duplicateManufacturers.length ? 'danger' : 'ok'} />
        <Stat label="Below $1,000" value={integrity.belowPriceFloor.length} tone={integrity.belowPriceFloor.length > 1 ? 'warn' : 'ok'} />
        <Stat label="Missing provenance" value={integrity.missingProvenance.length} tone={integrity.missingProvenance.length ? 'warn' : 'ok'} />
        <Stat label="Cable overclaims" value={integrity.cableClaims.length} tone={integrity.cableClaims.length ? 'danger' : 'ok'} />
      </div>
      {integrity.cableClaims.length > 0 && (
        <Note tone="danger">
          Cable entries whose prose claims more than the engine computes:{' '}
          {integrity.cableClaims.join('; ')}.
        </Note>
      )}
      {integrity.belowPriceFloor.length > 0 && (
        <Note tone="warn">
          Below the $1,000 positioning floor:{' '}
          {integrity.belowPriceFloor.map((b) => `${b.label} (${b.amount.toFixed(0)}, ${b.status})`).join(', ')}. The
          Topping D90 is present because the specification names it as the default DAC. Cables are excluded from this
          floor by design -- comparing an inexpensive cable against an expensive one is a large part of what the cable
          database is for.
        </Note>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function RenderPanel() {
  const state = useApp();
  const [format, setFormat] = useState<'wav' | 'flac'>('wav');
  const [wavFormat, setWavFormat] = useState<WavFormat>('pcm24');
  const [progress, setProgress] = useState<{ stage: string; frac: number } | null>(null);
  const [result, setResult] = useState<{ bytes: Uint8Array; name: string; peak: number; clipped: boolean; ms: number; verify?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wholeFile, setWholeFile] = useState(true);
  const [limiter, setLimiter] = useState(false);

  const buffer = state.buffer;
  const chain = state.activeSystem === 'A' ? state.project.systemA : state.project.systemB;

  const render = async () => {
    if (!buffer) return;
    setError(null);
    setResult(null);
    setProgress({ stage: 'Preparing', frac: 0 });
    try {
      const r = await renderOffline({
        buffer,
        chain,
        dac: registry.get(chain.dacId) as DacProfile | null,
        amp: registry.get(chain.ampId) as AmpProfile | null,
        output: registry.get(chain.outputId) as OutputProfile | null,
        engine: state.project.engine,
        sourceRate: state.metadata?.sampleRate ?? buffer.sampleRate,
        resolveCable,
        startSec: wholeFile ? 0 : state.position,
        endSec: wholeFile ? buffer.duration : Math.min(buffer.duration, state.position + 30),
        masterGainDb: 0,
        limiter,
        onProgress: (stage, frac) => setProgress({ stage, frac }),
      });

      setProgress({ stage: format === 'flac' ? 'Encoding FLAC' : 'Writing WAV', frac: 0.92 });
      const base = (state.metadata?.fileName ?? 'render').replace(/\.[^.]+$/, '');
      const dac = registry.get(chain.dacId);
      const amp = registry.get(chain.ampId);
      const out = registry.get(chain.outputId);
      const tag = [dac?.model, amp?.model, out?.model].filter(Boolean).join(' + ').replace(/[^\w\s+.-]/g, '');

      let bytes: Uint8Array;
      let name: string;
      let verify: string | undefined;
      if (format === 'flac') {
        const bits = wavFormat === 'pcm16' ? 16 : 24;
        bytes = encodeFlac(r.channels, {
          sampleRate: r.sampleRate,
          bitsPerSample: bits,
          onProgress: (f) => setProgress({ stage: 'Encoding FLAC', frac: 0.9 + f * 0.1 }),
        });
        const v = verifyFlacHeader(bytes);
        verify = v.ok ? `Valid FLAC stream: ${v.detail}` : `FLAC verification FAILED: ${v.detail}`;
        name = `${base} [${tag}].flac`;
      } else {
        bytes = writeWav(r.channels, { sampleRate: r.sampleRate, format: wavFormat });
        const back = readWav(bytes);
        verify = back ? `WAV reads back as ${back.sampleRate} Hz, ${back.channels.length} ch` : 'WAV verification failed';
        name = `${base} [${tag}].wav`;
      }

      setResult({ bytes, name, peak: r.peakDbFS, clipped: r.clipped, ms: r.renderMs, verify });
      setProgress(null);
      state.log('info', `Rendered ${r.durationSec.toFixed(1)} s at ${r.settingsUsed.oversample}x oversampling in ${(r.renderMs / 1000).toFixed(1)} s. Peak ${r.peakDbFS.toFixed(2)} dBFS.`);
    } catch (err) {
      setProgress(null);
      setError((err as Error).message);
    }
  };

  return (
    <>
      <Note>
        Offline render uses the same worklets as live playback but at maximum-quality settings: higher oversampling,
        longer filters, more biquad sections. It is not a different engine, so the export matches what you auditioned,
        only computed more carefully.
      </Note>

      <SectionTitle>Source</SectionTitle>
      {!buffer && <p className="dim">Load a file first.</p>}
      {buffer && (
        <div className="stat-grid">
          <Stat label="File" value={state.metadata?.fileName ?? '--'} />
          <Stat label="Duration" value={`${buffer.duration.toFixed(2)} s`} />
          <Stat label="Rate" value={`${buffer.sampleRate} Hz`} />
          <Stat label="Channels" value={buffer.numberOfChannels} />
        </div>
      )}

      <SectionTitle>Chain</SectionTitle>
      <div className="stat-grid">
        <Stat label="DAC" value={registry.get(chain.dacId)?.model ?? '--'} />
        <Stat label="Amplifier" value={registry.get(chain.ampId)?.model ?? '--'} />
        <Stat label="Output" value={registry.get(chain.outputId)?.model ?? '--'} />
      </div>

      <SectionTitle>Output format</SectionTitle>
      <div className="row wrap" style={{ gap: 10 }}>
        <Segmented
          value={format}
          onChange={setFormat}
          options={[
            { value: 'wav', label: 'WAV' },
            { value: 'flac', label: 'FLAC' },
          ]}
          ariaLabel="Container"
        />
        <Segmented
          value={wavFormat}
          onChange={setWavFormat}
          options={
            format === 'flac'
              ? [
                  { value: 'pcm16' as WavFormat, label: '16 bit' },
                  { value: 'pcm24' as WavFormat, label: '24 bit' },
                ]
              : [
                  { value: 'pcm16' as WavFormat, label: '16 bit' },
                  { value: 'pcm24' as WavFormat, label: '24 bit' },
                  { value: 'pcm32' as WavFormat, label: '32 bit int' },
                  { value: 'float32' as WavFormat, label: '32 bit float' },
                ]
          }
          ariaLabel="Bit depth"
        />
        <Toggle label="Whole file" checked={wholeFile} onChange={setWholeFile} />
        <Toggle label="Apply safety limiter" checked={limiter} onChange={setLimiter} />
      </div>
      <p className="faint tiny">
        FLAC is encoded here in the browser with a real lossless encoder (fixed predictors, Rice coding, CRC and MD5).
        Your audio never leaves this machine.
      </p>

      <div className="row" style={{ marginTop: 10 }}>
        <button className="btn primary" onClick={() => void render()} disabled={!buffer || progress !== null}>
          {progress ? <Spinner /> : '⏺'} Render
        </button>
        {result && (
          <button
            className="btn"
            onClick={() => downloadBytes(result.bytes, result.name, format === 'flac' ? 'audio/flac' : 'audio/wav')}
          >
            ⬇ Download {fmtBytes(result.bytes.length)}
          </button>
        )}
      </div>

      {progress && (
        <div style={{ marginTop: 10 }}>
          <div className="row small dim">
            <span>{progress.stage}</span>
            <div className="spacer" />
            <span className="mono">{(progress.frac * 100).toFixed(0)}%</span>
          </div>
          <div className="bar" style={{ marginTop: 4 }}>
            <span style={{ width: `${progress.frac * 100}%` }} />
          </div>
        </div>
      )}

      {error && <Note tone="danger">{error}</Note>}

      {result && (
        <>
          <SectionTitle>Result</SectionTitle>
          <div className="stat-grid">
            <Stat label="Size" value={fmtBytes(result.bytes.length)} />
            <Stat label="Peak" value={`${result.peak.toFixed(2)} dBFS`} tone={result.clipped ? 'danger' : 'ok'} />
            <Stat label="Render time" value={`${(result.ms / 1000).toFixed(2)} s`} />
            <Stat label="Clipped" value={result.clipped ? 'YES' : 'no'} tone={result.clipped ? 'danger' : 'ok'} />
          </div>
          {result.verify && <Note tone={result.verify.includes('FAILED') ? 'danger' : undefined}>{result.verify}</Note>}
          {result.clipped && (
            <Note tone="warn">
              The render clipped. Lower the amplifier volume or the DAC trim and render again — the file was NOT
              normalised, because silently changing the level would misrepresent the chain.
            </Note>
          )}
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function MeasurementPanel() {
  const state = useApp();
  const [sweep, setSweep] = useState<SweepMeasurement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [stored, setStored] = useState<storage.StoredMeasurement[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void storage.listMeasurements().then(setStored);
  }, []);

  const chain = state.activeSystem === 'A' ? state.project.systemA : state.project.systemB;

  const runSweep = async () => {
    setBusy(true);
    setErr(null);
    try {
      const m = await measureChain({
        chain,
        dac: registry.get(chain.dacId) as DacProfile | null,
        amp: registry.get(chain.ampId) as AmpProfile | null,
        output: registry.get(chain.outputId) as OutputProfile | null,
        engine: state.project.engine,
        sourceRate: state.metadata?.sampleRate ?? 48000,
        resolveCable,
        targetRate: 48000,
        seconds: 6,
      });
      setSweep(m);
      state.log('info', `Swept the chain: THD ${m.thdPercent.toFixed(4)}%, noise floor ${m.noiseFloorDb.toFixed(1)} dB.`);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Note tone="warn">
        This measures the MODEL, not hardware. It runs a log sweep through the currently selected chain inside an
        offline context and deconvolves it. It is a simulated measurement of a simulation, and it is labelled that way
        everywhere it appears. Measuring a physical device needs an interface, a reference microphone and a calibrated
        signal path, none of which a web page has.
      </Note>

      <SectionTitle>Simulated sweep of the current chain</SectionTitle>
      <div className="row">
        <button className="btn primary" onClick={() => void runSweep()} disabled={busy}>
          {busy ? <Spinner /> : '∿'} Run 10 Hz - 24 kHz sweep
        </button>
        {sweep && (
          <span className="mono small dim">
            THD {sweep.thdPercent.toFixed(4)}% · noise {sweep.noiseFloorDb.toFixed(1)} dB
          </span>
        )}
      </div>
      {err && <Note tone="danger">{err}</Note>}

      {sweep && (
        <>
          <div style={{ marginTop: 10 }}>
            <FrequencyPlot
              series={[{ freq: sweep.freq, values: sweep.magDb, color: '#45c8b8' }]}
              dbMin={-24}
              dbMax={12}
              height={200}
              yLabel="dB re 1 kHz"
            />
          </div>
          <div className="grid2" style={{ marginTop: 10 }}>
            <div>
              <div className="tiny faint">Phase</div>
              <FrequencyPlot series={[{ freq: sweep.freq, values: sweep.phaseDeg, color: '#b48ead' }]} dbMin={-720} dbMax={720} height={140} yLabel="deg" />
            </div>
            <div>
              <div className="tiny faint">Group delay</div>
              <FrequencyPlot series={[{ freq: sweep.freq, values: sweep.groupDelayMs, color: '#ffc857' }]} dbMin={-10} dbMax={40} height={140} yLabel="ms" />
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <div className="tiny faint">Deconvolved impulse response</div>
            <ImpulsePlot taps={sweep.impulse.subarray(0, 2048)} height={130} sampleRate={sweep.sampleRate} />
          </div>
          <SectionTitle>Harmonic products (separated by the sweep)</SectionTitle>
          <table className="data">
            <thead>
              <tr>
                <th>Order</th>
                <th className="num">Level</th>
                <th className="num">Percent</th>
              </tr>
            </thead>
            <tbody>
              {sweep.harmonicDb.map((h) => (
                <tr key={h.order}>
                  <td>H{h.order}</td>
                  <td className="num">{h.levelDb > -160 ? `${h.levelDb.toFixed(1)} dB` : 'below floor'}</td>
                  <td className="num">{h.levelDb > -160 ? `${(Math.pow(10, h.levelDb / 20) * 100).toFixed(4)}%` : '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <SectionTitle>Import measurement data</SectionTitle>
      <p className="dim small">
        Frequency-response CSV, impulse-response WAV and JSON profiles are accepted. Imported curves flow through
        exactly the same compiler as the built-in ones, so a measured profile needs no engine change.
      </p>
      <input
        ref={fileRef}
        type="file"
        accept=".json,.csv,.txt,.wav"
        style={{ display: 'none' }}
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          try {
            const r = await importProfileJson(f);
            if (r.profile) {
              registry.register([r.profile]);
              await storage.saveCustomProfile(r.profile);
              state.log('info', `Imported profile ${r.profile.manufacturer} ${r.profile.model}. It is now in the library.`);
            } else if (r.measurement) {
              await storage.saveMeasurement(r.measurement);
              setStored(await storage.listMeasurements());
              state.log('info', `Imported measurement "${r.measurement.name}" (${r.measurement.kind}).`);
            }
            for (const w of r.warnings) state.log('warn', w);
          } catch (err) {
            state.log('error', `Import failed: ${(err as Error).message}`);
          }
        }}
      />
      <div className="row">
        <button className="btn" onClick={() => fileRef.current?.click()}>
          ⬆ Import file
        </button>
        <button
          className="btn"
          onClick={() => {
            const p = registry.get(chain.dacId);
            if (!p) return;
            const json = exportProfileJson(p);
            downloadBytes(new TextEncoder().encode(json), `${p.id}.json`, 'application/json');
          }}
        >
          ⬇ Export current DAC profile
        </button>
      </div>

      {stored.length > 0 && (
        <>
          <SectionTitle>Stored measurements</SectionTitle>
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {stored.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td className="dim">{m.kind}</td>
                  <td className="dim">{new Date(m.created).toLocaleString()}</td>
                  <td>
                    <button
                      className="btn sm"
                      onClick={() => void storage.deleteMeasurement(m.id).then(() => storage.listMeasurements().then(setStored))}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function TestSignalPanel() {
  const [mode, setMode] = useState<'off' | 'sine' | 'sweep' | 'white' | 'pink' | 'impulse' | 'step' | 'multitone'>('off');
  const [freq, setFreq] = useState(1000);
  const [level, setLevel] = useState(-20);
  const [channels, setChannels] = useState<'both' | 'left' | 'right'>('both');
  const gen = useApp((s) => s.log);

  const apply = (next: Partial<{ mode: typeof mode; freq: number; level: number; channels: typeof channels }>) => {
    const m = next.mode ?? mode;
    const f = next.freq ?? freq;
    const l = next.level ?? level;
    const c = next.channels ?? channels;
    engine.setTestSignal({
      mode: m,
      freq: m === 'sweep' ? 20 : f,
      freq2: 20000,
      sweepSeconds: 8,
      level: Math.pow(10, l / 20),
      channels: c,
      multitone: [55, 110, 220, 440, 880, 1760, 3520, 7040, 14080],
      restart: true,
    });
    if (m !== 'off') gen('info', `Test signal: ${m} at ${l} dBFS through the full hardware chain.`);
  };

  return (
    <>
      <Note>
        Test signals pass through the complete hardware chain, exactly as music does. This is how you check that a model
        is doing what it claims: feed a 1 kHz sine into a SET amplifier and the harmonic structure should appear in the
        analyser spectrum at the level the profile declares.
      </Note>

      <SectionTitle>Generator</SectionTitle>
      <Segmented
        value={mode}
        onChange={(v) => {
          setMode(v);
          apply({ mode: v });
        }}
        options={[
          { value: 'off', label: 'Off' },
          { value: 'sine', label: 'Sine' },
          { value: 'sweep', label: 'Sweep' },
          { value: 'white', label: 'White' },
          { value: 'pink', label: 'Pink' },
          { value: 'impulse', label: 'Impulse' },
          { value: 'step', label: 'Step' },
          { value: 'multitone', label: 'Multitone' },
        ]}
        ariaLabel="Test signal"
      />

      <div style={{ marginTop: 10 }}>
        <Slider
          label="Frequency"
          min={20}
          max={20000}
          step={1}
          value={freq}
          onChange={(v) => {
            setFreq(v);
            apply({ freq: v });
          }}
          format={(v) => fmtHz(v)}
          disabled={mode !== 'sine'}
        />
        <Slider
          label="Level"
          min={-90}
          max={0}
          step={0.5}
          value={level}
          onChange={(v) => {
            setLevel(v);
            apply({ level: v });
          }}
          format={(v) => `${v.toFixed(1)} dBFS`}
        />
        <div className="field">
          <label>Channels</label>
          <Segmented
            small
            value={channels}
            onChange={(v) => {
              setChannels(v);
              apply({ channels: v });
            }}
            options={[
              { value: 'both', label: 'Both' },
              { value: 'left', label: 'Left' },
              { value: 'right', label: 'Right' },
            ]}
            ariaLabel="Channels"
          />
        </div>
      </div>

      <Note tone="warn">
        Test signals can be far more fatiguing and far more dangerous to hearing than music at the same meter reading.
        Start low. The safety limiter stays active.
      </Note>
    </>
  );
}

// ---------------------------------------------------------------------------

function ConsolePanel() {
  const logs = useApp((s) => s.logs);
  const bottom = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<'all' | 'warn' | 'error'>('all');

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [logs.length]);

  const shown = logs.filter((l) => (filter === 'all' ? true : filter === 'warn' ? l.level !== 'info' : l.level === 'error'));

  return (
    <>
      <div className="row" style={{ marginBottom: 8 }}>
        <Segmented
          small
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: `All (${logs.length})` },
            { value: 'warn', label: `Warnings (${logs.filter((l) => l.level === 'warn').length})` },
            { value: 'error', label: `Errors (${logs.filter((l) => l.level === 'error').length})` },
          ]}
          ariaLabel="Log filter"
        />
        <div className="spacer" />
        <button
          className="btn sm"
          onClick={() => {
            const text = logs.map((l) => `${new Date(l.at).toISOString()} [${l.level}] ${l.message}`).join('\n');
            void navigator.clipboard?.writeText(text);
          }}
        >
          Copy
        </button>
      </div>
      <div className="console">
        {shown.map((l) => (
          <div className={`line ${l.level}`} key={l.id}>
            <span className="t">{new Date(l.at).toLocaleTimeString()}</span>
            <span className="m">{l.message}</span>
          </div>
        ))}
        <div ref={bottom} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------

function ProjectPanel() {
  const exportProject = useApp((s) => s.exportProject);
  const importProject = useApp((s) => s.importProject);
  const saveProjectAs = useApp((s) => s.saveProjectAs);
  const project = useApp((s) => s.project);
  const [name, setName] = useState(project.name);
  const [json, setJson] = useState('');

  return (
    <>
      <SectionTitle>Save</SectionTitle>
      <div className="row">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Project name" />
        <button className="btn" onClick={() => void saveProjectAs(name)}>
          Save locally
        </button>
        <button
          className="btn"
          onClick={() => downloadBytes(new TextEncoder().encode(exportProject()), `${name || 'project'}.dacengine.json`, 'application/json')}
        >
          ⬇ Export JSON
        </button>
      </div>

      <SectionTitle>Import</SectionTitle>
      <textarea
        rows={10}
        value={json}
        onChange={(e) => setJson(e.target.value)}
        placeholder="Paste a project JSON here..."
      />
      <button className="btn" style={{ marginTop: 6 }} onClick={() => importProject(json)} disabled={!json.trim()}>
        Import
      </button>
    </>
  );
}
